import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { listFirms, createShareLinks, getShareLinks, sendProjectInvitation, resolveOrganization } from '../../../services/projectApi';
import API_URL from '../../../config';
import './SharePanel.css';

const UNKNOWN_VALUE = '__unknown__';

const STAKEHOLDER_DEFS = [
  { metaKey: 'yapiDenetimId',     partyCode: 'building_inspector',    label: 'Yapi Denetim',            unknownLabel: 'Bilinmeyen Yapi Denetim' },
  { metaKey: 'elektrikDagitimId', partyCode: 'distribution_company',  label: 'Elektrik Dagitim Firmasi', unknownLabel: 'Bilinmeyen Dagitim Firmasi' },
  { metaKey: 'telekomId',         partyCode: 'telecom',               label: 'Telekom',                  unknownLabel: 'Bilinmeyen Telekom' },
  { metaKey: 'itfaiyeId',         partyCode: 'fire_department',       label: 'Itfaiye',                  unknownLabel: 'Bilinmeyen Itfaiye' },
  { metaKey: 'client',            partyCode: 'customer',              label: 'Musteri',                  unknownLabel: null, isText: true },
];

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const SharePanel = ({ isOpen, onClose }) => {
  const projectState = useSelector(state => state.project);
  const { projectId, projectName } = projectState;

  const [firms, setFirms] = useState([]);
  const [projectMeta, setProjectMeta] = useState(null);
  const [projectVersion, setProjectVersion] = useState(0);
  const [links, setLinks] = useState({});
  const [copied, setCopied] = useState(null);
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedOrgs, setResolvedOrgs] = useState({});

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('projabze_token');
      const [firmsList, existingLinks, projRes] = await Promise.all([
        listFirms(),
        getShareLinks(projectId).catch(() => []),
        token
          ? fetch(`${API_URL}/api/projects/${projectId}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.ok ? r.json() : null)
          : Promise.resolve(null),
      ]);

      setFirms(firmsList);

      const linkMap = {};
      for (const l of existingLinks) {
        linkMap[l.partyTypeId] = `${window.location.origin}/share/${l.token}`;
      }

      const meta = projRes?.project?.metadata || {};
      setProjectMeta(meta);
      setProjectVersion(projRes?.project?.currentVersion || 0);

      // Auto-generate missing share links for all stakeholders
      const missingParties = [];
      for (const def of STAKEHOLDER_DEFS) {
        const metaVal = meta[def.metaKey] || '';
        if (!metaVal) continue;
        const pid = `party-${def.partyCode.replace(/_/g, '-')}`;
        if (!linkMap[pid]) {
          missingParties.push({ partyTypeId: pid, accessLevel: 'view' });
        }
      }
      if (missingParties.length > 0) {
        try {
          const created = await createShareLinks(projectId, missingParties, 30);
          for (const l of created) {
            linkMap[l.partyTypeId] = `${window.location.origin}/share/${l.token}`;
          }
        } catch (e) {
          console.warn('Auto-generate share links failed:', e);
        }
      }

      setLinks(linkMap);

      // Resolve organizations for known firm-type stakeholders
      const orgMap = {};
      const FIRM_TYPE_MAP = {
        building_inspector: 'yapi_denetim',
        distribution_company: 'elektrik_dagitim',
        fire_department: 'itfaiye',
        telecom: 'telekom',
      };
      for (const def of STAKEHOLDER_DEFS) {
        if (def.isText) continue;
        const ft = FIRM_TYPE_MAP[def.partyCode];
        if (!ft) continue;
        const metaVal = meta[def.metaKey];
        if (!metaVal || metaVal === UNKNOWN_VALUE) continue;
        try {
          const orgs = await resolveOrganization({ firmType: ft, firmId: metaVal });
          if (orgs.length > 0) orgMap[def.metaKey] = orgs[0];
        } catch { /* ignore */ }
      }
      setResolvedOrgs(orgMap);
    } catch (err) {
      console.error('Share panel data fetch error:', err);
      setError('Veri yuklenirken hata olustu.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, fetchData]);

  if (!isOpen) return null;

  // Build stakeholder list
  const stakeholders = STAKEHOLDER_DEFS.map(def => {
    const metaVal = projectMeta?.[def.metaKey] || '';
    if (!metaVal) return null;

    let name;
    let isUnknown = false;

    if (def.isText) {
      name = metaVal;
    } else if (metaVal === UNKNOWN_VALUE) {
      name = def.unknownLabel;
      isUnknown = true;
    } else {
      const firm = firms.find(f => f.id === metaVal);
      if (firm) {
        name = firm.name;
      } else {
        name = def.unknownLabel;
        isUnknown = true;
      }
    }

    return { ...def, name, isUnknown, metaVal };
  }).filter(Boolean);

  const partyId = (code) => `party-${code.replace(/_/g, '-')}`;

  const handleCopy = (key, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleSend = async (key, url, stakeholder) => {
    const resolved = resolvedOrgs[key];
    if (resolved) {
      try {
        const pid = `party-${stakeholder.partyCode.replace(/_/g, '-')}`;
        await sendProjectInvitation({
          projectId,
          targetOrganizationId: resolved.id,
          partyTypeId: pid,
          message: `${projectName} projesi icin inceleme daveti.`
        });
        setSent(key);
        setTimeout(() => setSent(null), 3000);
      } catch (err) {
        console.error('Send invitation error:', err);
        // Fallback: copy link
        navigator.clipboard.writeText(url);
        setSent(key);
        setTimeout(() => setSent(null), 2000);
      }
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setSent(key);
        setTimeout(() => setSent(null), 2000);
      });
    }
  };

  // ─── Not saved yet ───
  if (!projectId) {
    return (
      <div className="share-overlay" onClick={onClose}>
        <div className="share-panel" onClick={e => e.stopPropagation()}>
          <div className="share-header">
            <h3>Paylas</h3>
            <button className="share-close" onClick={onClose}>&times;</button>
          </div>
          <div className="share-body">
            <div className="share-empty">Once projeyi kaydedin.</div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Project saved but no version (needs at least one save from editor) ───
  if (projectVersion === 0 && !loading) {
    return (
      <div className="share-overlay" onClick={onClose}>
        <div className="share-panel" onClick={e => e.stopPropagation()}>
          <div className="share-header">
            <h3>Paylas — {projectName}</h3>
            <button className="share-close" onClick={onClose}>&times;</button>
          </div>
          <div className="share-body">
            <div className="share-empty">
              Paylasim icin projeyi en az bir kez kaydedin (Ctrl+S).
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-panel" onClick={e => e.stopPropagation()}>
        <div className="share-header">
          <h3>Paylas — {projectName}</h3>
          <button className="share-close" onClick={onClose}>&times;</button>
        </div>

        <div className="share-body">
          {loading ? (
            <div className="share-empty">Yukleniyor...</div>
          ) : error ? (
            <div className="share-empty share-error">{error}</div>
          ) : stakeholders.length === 0 ? (
            <div className="share-empty">
              Proje paydasi bulunamadi.<br/>
              <span className="share-hint">Projelerim sayfasindan paydaslari ekleyin.</span>
            </div>
          ) : (
            <div className="share-list">
                {stakeholders.map(s => {
                  const pid = partyId(s.partyCode);
                  const linkUrl = links[pid];

                  return (
                    <div className="share-item" key={s.metaKey}>
                      <div className="share-item-info">
                        <span className="share-item-label">{s.label}</span>
                        <span className={`share-item-name ${s.isUnknown ? 'unknown' : ''}`}>
                          {s.name}
                        </span>
                      </div>
                      {resolvedOrgs[s.metaKey] && (
                        <div className="share-org-badge">Projabze kayitli: {resolvedOrgs[s.metaKey].name}</div>
                      )}
                      <div className="share-item-action">
                        {(s.isUnknown || s.isText) ? (
                          linkUrl ? (
                            <div className="share-link-row">
                              <input
                                className="share-link-input"
                                value={linkUrl}
                                readOnly
                                onClick={e => e.target.select()}
                              />
                              <button
                                className={`share-copy-btn ${copied === s.metaKey ? 'copied' : ''}`}
                                onClick={() => handleCopy(s.metaKey, linkUrl)}
                                title="Kopyala"
                              >
                                {copied === s.metaKey ? <CheckIcon /> : <CopyIcon />}
                              </button>
                            </div>
                          ) : (
                            <span className="share-generating-text">Olusturuluyor...</span>
                          )
                        ) : (
                          <button
                            className={`share-send-btn ${sent === s.metaKey ? 'sent' : ''} ${resolvedOrgs[s.metaKey] ? 'org-registered' : ''}`}
                            onClick={() => handleSend(s.metaKey, linkUrl, s)}
                            disabled={!linkUrl}
                          >
                            {sent === s.metaKey ? 'Gonderildi ✓' : !linkUrl ? 'Olusturuluyor...' : resolvedOrgs[s.metaKey] ? 'Davet Gonder' : 'Gonder'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharePanel;
